import { type ReactElement } from 'react';
import { type SetupItemReadingView, type SetupItemState, type SetupReadinessView, type SetupUnavailableView } from '@vello/api-client';
import { Accordion, Badge, Button, Card, LoadingPanel, Notice, PageHeader, Panel, Progress, Row, SectionTitle, Stack, Text } from '@vello/web-theme';
import { fixRouteFor, groupSetupItems, progressOf, providerConsoleLinks, scopeTimeZone, screenAllowed, formatMarketInstant, setupItemKey, type SetupSection } from '../../src/index';
import { useRead } from '../components/use-read';
import { useAdmin } from '../session';
import { useRouter } from '../router';
import { useT } from '../i18n/provider';
import { useCountryName } from '../i18n/country-name';
import { useVocab } from '../i18n/vocab';

/**
 * Configurações › Configuração da plataforma (D615 SETUP-READINESS) — what is still missing for the platform to work end to end,
 * read by the server from the real runtime state (`GET /v1/admin/setup-readiness`), and the way to each fix.
 *
 *   - **Read only, never a colour from a guess.** Every item is the server's reading: its state, a stable reason code and the
 *     counts behind it. An item the operator may not read is absent; one that could not be checked now is listed as such.
 *   - **The scope is the header's.** The same market / country the menu counters read; this screen draws no selector of its own.
 *   - **Words, not codes.** The item, its about and steps, the state, the area, the level and the reason come from the central
 *     vocabulary (`setup.*`); a governed-setting key reads through `settings.key`.
 *   - **No primary action.** The screen informs; "Abrir a tela" and the vendor link are secondary. The vendor consoles are
 *     external (https, new tab, `noopener`).
 */
const STATE_TONE: Readonly<Record<SetupItemState, 'success' | 'warning' | 'danger' | 'neutral'>> = {
  ok: 'success',
  missing: 'danger',
  invalid: 'danger',
  incomplete: 'warning',
  disabled: 'warning',
  attention: 'warning',
  not_built: 'neutral',
  unknown: 'neutral',
};

type Place = Pick<SetupItemReadingView, 'level' | 'country' | 'market'>;

function usePlaceText(): (place: Place) => string {
  const { markets } = useAdmin();
  const { v } = useVocab();
  const { t } = useT();
  const countryName = useCountryName();
  return (place) => {
    // Never a raw id: a market the shell does not know by name reads as "city without a registered name"; a country by its name.
    const where =
      place.market !== undefined ? markets.find((m) => m.id === place.market)?.name ?? t('setup.place.unnamed') : place.country !== undefined ? countryName(place.country) : undefined;
    return where === undefined ? v('setup.level', place.level) : `${v('setup.level', place.level)} · ${where}`;
  };
}

function SetupItemCard({ item, timeZone }: { item: SetupItemReadingView; timeZone: string | undefined }): ReactElement {
  const { grants } = useAdmin();
  const { navigate } = useRouter();
  const { t, lang } = useT();
  const { v } = useVocab();
  const placeText = usePlaceText();
  const route = fixRouteFor(item);
  const canOpen = route !== undefined && screenAllowed(grants, route);
  const links = providerConsoleLinks(item);
  const detail = item.detail;
  const vars = {
    ...(detail?.count !== undefined ? { count: detail.count } : {}),
    ...(detail?.total !== undefined ? { total: detail.total } : {}),
  };
  const steps = v('setup.item_steps', item.id).split('\n').map((s) => s.trim()).filter((s) => s !== '');
  return (
    <Card as="article">
      <Stack gap={3}>
        <Row justify="between" wrap gap={3}>
          <Stack gap={1}>
            <Text role="title" as="h4">{v('setup.item', item.id)}</Text>
            <Text role="caption" tone="secondary">{placeText(item)}</Text>
          </Stack>
          <Badge tone={STATE_TONE[item.state]}>{v('setup.state', item.state)}</Badge>
        </Row>
        <Text as="p">{v('setup.reason', item.reasonCode, vars)}</Text>
        {detail?.keys !== undefined && detail.keys.length > 0 && (
          <Stack gap={1}>
            <Text role="label" tone="secondary">{t('setup.keys.title')}</Text>
            <Stack as="ul" gap={1} className="vello-list-bulleted">
              {detail.keys.map((key) => (
                <li key={key}><Text>{v('settings.key', key)}</Text></li>
              ))}
            </Stack>
          </Stack>
        )}
        {(item.updatedAt !== undefined || item.verifiedAt !== undefined) && (
          <Row gap={4} wrap>
            {item.updatedAt !== undefined && <Text role="caption" tone="secondary">{t('setup.updatedAt', { when: formatMarketInstant(item.updatedAt, timeZone, lang) })}</Text>}
            {item.verifiedAt !== undefined && <Text role="caption" tone="secondary">{t('setup.verifiedAt', { when: formatMarketInstant(item.verifiedAt, timeZone, lang) })}</Text>}
          </Row>
        )}
        <Accordion
          headingLevel={4}
          items={[
            {
              id: 'help',
              title: t('setup.help.title'),
              content: (
                <Stack gap={3}>
                  <Text as="p">{v('setup.item_about', item.id)}</Text>
                  {steps.length > 0 && (
                    <Stack gap={2}>
                      <Text role="label" tone="secondary">{t('setup.help.steps')}</Text>
                      <Stack as="ol" gap={2} className="vello-list-ordered">
                        {steps.map((step, i) => (
                          <li key={i}><Text>{step}</Text></li>
                        ))}
                      </Stack>
                    </Stack>
                  )}
                </Stack>
              ),
            },
          ]}
        />
        {(canOpen || links.length > 0) && (
          <Row gap={3} wrap>
            {canOpen && route !== undefined && <Button variant="secondary" onClick={() => navigate(route)}>{t('setup.open')}</Button>}
            {links.map((link) => (
              <a key={link.url} className="vello-link" href={link.url} target="_blank" rel="noopener noreferrer">
                {t('setup.vendor', { vendor: link.vendor })}
              </a>
            ))}
          </Row>
        )}
      </Stack>
    </Card>
  );
}

function CompactRow({ item, showReason }: { item: SetupItemReadingView; showReason: boolean }): ReactElement {
  const { v } = useVocab();
  const placeText = usePlaceText();
  return (
    <Row justify="between" wrap gap={3} as="li">
      <Stack gap={1}>
        <Text>{v('setup.item', item.id)}</Text>
        <Text role="caption" tone="secondary">{showReason ? `${placeText(item)} · ${v('setup.reason', item.reasonCode)}` : placeText(item)}</Text>
      </Stack>
      <Badge tone={STATE_TONE[item.state]}>{v('setup.state', item.state)}</Badge>
    </Row>
  );
}

function SetupSectionView({ section, timeZone }: { section: SetupSection; timeZone: string | undefined }): ReactElement {
  const { t } = useT();
  const { v } = useVocab();
  const nf = new Intl.NumberFormat(useT().lang);
  const folded = [
    ...(section.done.length > 0
      ? [{ id: 'done', title: <span className="tnum">{t('setup.done', { count: nf.format(section.done.length) })}</span>, content: <Stack as="ul" gap={3}>{section.done.map((i) => <CompactRow key={setupItemKey(i)} item={i} showReason={false} />)}</Stack> }]
      : []),
    ...(section.informative.length > 0
      ? [{ id: 'informative', title: <span className="tnum">{t('setup.informative', { count: nf.format(section.informative.length) })}</span>, content: <Stack as="ul" gap={3}>{section.informative.map((i) => <CompactRow key={setupItemKey(i)} item={i} showReason />)}</Stack> }]
      : []),
  ];
  return (
    <Stack as="section" gap={3}>
      <SectionTitle level={2}>{v('setup.area', section.area)}</SectionTitle>
      {section.pending.map((item) => (
        <SetupItemCard key={setupItemKey(item)} item={item} timeZone={timeZone} />
      ))}
      {folded.length > 0 && <Accordion headingLevel={3} multiple items={folded} />}
    </Stack>
  );
}

function UnavailablePanel({ items }: { items: readonly SetupUnavailableView[] }): ReactElement {
  const { t } = useT();
  const { v } = useVocab();
  const placeText = usePlaceText();
  return (
    <Panel>
      <Stack gap={3}>
        <SectionTitle level={2} size="sub">{t('setup.unavailable.title')}</SectionTitle>
        <Stack as="ul" gap={3}>
          {items.map((i) => (
            <li key={setupItemKey(i)}>
              <Stack gap={1}>
                <Text>{v('setup.item', i.id)}</Text>
                <Text role="caption" tone="secondary">{`${placeText(i)} · ${t('setup.unavailable.note')}`}</Text>
              </Stack>
            </li>
          ))}
        </Stack>
      </Stack>
    </Panel>
  );
}

function SummaryPanel({ view, timeZone, onRecheck }: { view: SetupReadinessView; timeZone: string | undefined; onRecheck: () => void }): ReactElement {
  const { t, tp, lang } = useT();
  const progress = progressOf(view.summary);
  const sentence = progress.empty
    ? t('setup.summary.empty')
    : progress.complete
      ? t('setup.summary.done')
      : progress.unverifiedOnly
        ? tp('setup.summary.unverified', progress.unverified)
        : tp('setup.summary.pending', progress.pending);
  return (
    <Panel>
      <Stack gap={3}>
        {!progress.empty && (
          <Stack gap={2}>
            <Progress
              label={t('setup.progress.label', { ready: progress.ready, required: progress.required })}
              value={progress.ready}
              max={progress.required}
              tone={progress.tone}
              size="lg"
            />
            <Text role="caption" tone="secondary" className="tnum">{t('setup.progress.label', { ready: progress.ready, required: progress.required })}</Text>
          </Stack>
        )}
        <Text role="body-lg" as="p" className="tnum">{sentence}</Text>
        <Row justify="between" wrap gap={3}>
          <Text role="caption" tone="secondary" className="tnum">{t('setup.evaluatedAt', { when: formatMarketInstant(view.evaluatedAt, timeZone, lang, 'datetime-seconds') })}</Text>
          <Button variant="secondary" onClick={onRecheck}>{t('setup.recheck')}</Button>
        </Row>
      </Stack>
    </Panel>
  );
}

export function PlatformSetupScreen(): ReactElement {
  const { runtime, scope, grants, markets } = useAdmin();
  const { t } = useT();
  const city = scope.current.city;
  const country = scope.current.country;
  const timeZone = scopeTimeZone(scope.current, markets);
  const { state, reload } = useRead<SetupReadinessView>(() => runtime.setupReadiness.get(city, country), [runtime, city, country, grants]);

  return (
    <>
      <PageHeader title={t('setup.title')} subtitle={t('setup.subtitle')} />
      {state === undefined || state.status === 'loading' ? (
        <LoadingPanel label={t('ui.loading')} rows={5} />
      ) : state.status === 'error' ? (
        <Panel>
          <Stack gap={3} align="start">
            <Notice tone="danger">{state.code === 'FORBIDDEN' ? t('w6.refused.forbidden') : t('setup.error')}</Notice>
            <Button variant="secondary" onClick={reload}>{t('ui.retry')}</Button>
          </Stack>
        </Panel>
      ) : (
        <Stack gap={5}>
          <SummaryPanel view={state.value} timeZone={timeZone} onRecheck={reload} />
          {state.value.items.length === 0 && state.value.unavailable.length === 0 && <Notice tone="neutral">{t('setup.noItems')}</Notice>}
          {groupSetupItems(state.value).map((section) => (
            <SetupSectionView key={section.area} section={section} timeZone={timeZone} />
          ))}
          {state.value.unavailable.length > 0 && <UnavailablePanel items={state.value.unavailable} />}
        </Stack>
      )}
    </>
  );
}
